import { formatUnits } from "ethers-v6";
import { getConfig } from "../constants/config";
import tokenService from "../../common/tokenService";

/**
 * Calculates the total input amount from a route array.
 *
 * @param {Array} route - The route array from getRoute response
 *
 * @returns {Promise<Object>} An object containing:
 *   - amountIn {bigint|undefined}: Total input amount in raw units
 *   - amountInFormatted {string|undefined}: Total input amount formatted with token decimals
 *   - tokenIn {string|undefined}: Input token address
 *   - tokenOut {string|undefined}: Output token address
 *   Returns object with all undefined values if route is invalid
 *
 * @example
 * // Example 1: Get total input for a single path route
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1000000000000000000" // 1 WSEI
 * });
 * const { amountIn, amountInFormatted } = await getTotalAmountIn(route);
 * console.log(amountInFormatted); // "1.0"
 *
 * @example
 * // Example 2: Get total input for a multi-path route
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xdAC17F958D2ee523a2206206994597C13D831ec7", // USDT
 *   amount: "100000000000000000000" // 100 WSEI
 * });
 * const { amountIn, amountInFormatted } = await getTotalAmountIn(route);
 * console.log(amountInFormatted); // "100.0"
 * @note For multi-path routes, this sums up amounts across all paths
 */
export const getTotalAmountIn = async (route) => {
  if (!route || !Array.isArray(route)) {
    console.error("Invalid or missing `route`: Must be an array.");
    return {
      amountIn: undefined,
      amountInFormatted: undefined,
      tokenIn: undefined,
      tokenOut: undefined,
    };
  }

  // Get config for non-token data
  const config = getConfig();
  const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
    config;

  // Get updated token list with user's configuration applied
  const tokens = await tokenService.getAsyncTokenList(
    chainId,
    additionalTokens,
    overrideDefaultTokens,
    nativeAddress
  );

  const tokenIn = tokens[route[0][0].tokenIn];
  const tokenOut = tokens[route[0].at(-1).tokenOut];

  let totalAmountIn = BigInt(0);

  for (let i = 0; i < route.length; i++) {
    totalAmountIn = totalAmountIn + BigInt(route[i][0]?.amountIn || 0);
  }

  return {
    amountIn: totalAmountIn,
    amountInFormatted: formatUnits(
      totalAmountIn,
      tokenIn.attributes.decimals
    ).toString(),
    tokenIn: tokenIn.attributes.address,
    tokenOut: tokenOut.attributes.address,
  };
};
